import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { IconButton } from "@cmp-src/primitives/icon-button/IconButton";
import { formatDistanceToNow } from "date-fns";
import { Edit, Trash } from "lucide-react";
import { CommentAttachmentsList } from "./CommentAttachmentsList";
import { CommentEditor } from "./CommentEditor";
import { CommentProps } from "./types";

export const Comment = ({
  comment,
  isOwnComment,
  editingCommentId,
  editingCommentText,
  onStartEdit,
  onCancelEdit,
  onEdit,
  onDelete,
  updateCommentLoading,
  onEditingCommentTextChange
}: CommentProps & { onEditingCommentTextChange?: (value: string) => void }) => {
  const isEditing = editingCommentId === comment.id;
  const attachments = comment.attachments ?? [];

  return (
    <div className="group flex space-x-3">
      <UserAvatar
        user={{
          name: comment.user.name,
          email: comment.user.email,
          profile_photo_url: comment.user.profile_photo_url
        }}
        size="sm"
        className="flex-shrink-0"
      />
      <div className="flex-1">
        <div className="mb-2 flex items-center justify-start">
          <div className="flex items-center space-x-2">
            <span className="font-inter font-normal text-neutral-900 text-sm">
              {comment.user.name}
            </span>
            <span className="text-neutral-700 text-sm">
              {formatDistanceToNow(new Date(comment.created), {
                addSuffix: true
              })}
              {comment.updated && comment.updated !== comment.created && (
                <span className="text-neutral-400">
                  {" "}
                  (updated{" "}
                  {formatDistanceToNow(new Date(comment.updated), {
                    addSuffix: true
                  })}
                  )
                </span>
              )}
            </span>
            {isOwnComment && !isEditing && (
              <div className="flex items-center space-x-1">
                <IconButton
                  icon={Edit}
                  variant="neutral"
                  size="xs"
                  showOnHover
                  onClick={() => onStartEdit(comment)}
                  aria-label="Edit comment"
                />
                <IconButton
                  icon={Trash}
                  variant="danger"
                  size="xs"
                  showOnHover
                  onClick={() => onDelete(comment.id)}
                  aria-label="Delete comment"
                />
              </div>
            )}
          </div>
        </div>
        {isEditing ? (
          <CommentEditor
            // Remounts on every saved revision so the editor always starts from
            // the comment's current attachments.
            key={`${comment.id}-${comment.updated}`}
            value={editingCommentText}
            onChange={(value) => onEditingCommentTextChange?.(value)}
            initialAttachments={attachments}
            onCancel={onCancelEdit}
            onSave={(nextAttachments) => onEdit(comment.id, nextAttachments)}
            isSaving={updateCommentLoading}
          />
        ) : (
          <>
            {comment.comment ? (
              <p className="w-fit max-w-full whitespace-pre-wrap rounded-2xl bg-neutral-100 px-4 py-2.5 font-normal text-neutral-800 text-sm leading-relaxed">
                {comment.comment}
              </p>
            ) : null}
            <CommentAttachmentsList attachments={attachments} />
          </>
        )}
      </div>
    </div>
  );
};
